import { LOGIN } from "../api/LoginApi";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Shield, Mail, Lock, User, Building } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated, refreshUser } = useAuth(); // NEW: refreshUser added
  const [isLoading, setIsLoading] = useState(false);

  // Login form
  const [loginData, setLoginData] = useState({ email: "", password: "" });

  // Register form
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    name: "",
    role: "viewer",
    department: "General"
  });

  const handleLogin = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      const response = await LOGIN(
        loginData.email,
        loginData.password
      );

      console.log(response.data);

      localStorage.setItem("token", response.data.access_token);
      setIsAuthenticated(true);

      // NEW: fetch the real logged-in user (name/email/role) right away,
      // so the Header shows correct data immediately instead of waiting for a page refresh
      await refreshUser();

      navigate("/dashboard");
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#8c3add] to-[#6b21a8] p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#1fa7a1] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <Shield className="h-10 w-10 text-white" />
            <span className="text-3xl font-bold text-white tracking-tight">Culsight</span>
          </div>

          <div className="space-y-6">
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
              Phishing Simulation<br />& Security Awareness
            </h1>
            <p className="text-white/80 text-lg max-w-md">
              Protect your organization with advanced phishing simulations,
              comprehensive risk analytics, and behavioral training programs.
            </p>
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-6">
          {[
            { label: "Threats Detected", value: "2.5K+" },
            { label: "Users Trained", value: "15K+" },
            { label: "Risk Reduction", value: "73%" }
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl font-bold text-white">{stat.value}</p>
              <p className="text-white/70 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#fefbff]">
        <Card className="w-full max-w-md border-0 shadow-xl bg-white">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <Shield className="h-8 w-8 text-[#8c3add]" />
              <span className="text-2xl font-bold">
                <span className="text-[#8c3add]">Cul</span>
                <span className="text-[#1f1f23]">sight</span>
              </span>
            </div>
            <CardTitle className="text-2xl font-bold text-[#1f1f23]">Welcome</CardTitle>
            <CardDescription className="text-gray-500">
              Sign in to access your security dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              {/* <TabsList className="grid w-full grid-cols-2 mb-6"> */}
              {/* <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger> */}

              {/* </TabsList> */}

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-sm font-medium text-gray-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="admin@culsight.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        data-testid="login-email"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-sm font-medium text-gray-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        data-testid="login-password"
                        required
                      />
                    </div>
                  </div>
                  {/* <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                    <strong>Demo credentials:</strong><br />
                    Email: admin@culsight.com<br />
                    Password: admin123
                  </div> */}
                  <Button
                    type="submit"
                    className="w-full bg-[#8c3add] hover:bg-[#702eb0] text-white font-medium py-5"
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>


            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;